import { useState } from 'react';
import chroma from 'chroma-js';
import classNames from 'classnames';
import Text from '@ui/text/text/text';
import type { QuillColorPanelProps } from '@ui/quill/quill.types';
import { EPaletteModes, type IColorVariants, type TColorItemArray } from '@/types/theme-packs.types';
import { parseColor } from '../../utils/utils';
import { useAppSelector } from '@/store/hooks';
import {
  selectCampaignEditorCurrentTemplateId,
  selectCampaignEditorTheme,
} from '@/store/slices/campaign-editor/selectors';
import { templateStaticDefaults } from '../../template-defaults';
import classes from './quill-color-panel.module.css';
import PillTabs from '@ui/tabs/pill-tabs';
import ComingSoonImage from '@/micros/coming-soon/coming-soon-image/coming-soon-image';

type Tab = 'theme' | 'custom';

const TABS = [
  { value: 'theme', label: 'Theme' },
  { value: 'custom', label: 'Custom' },
];

// the palette's colors in the order the design lays them out
const SWATCH_SLOTS: (keyof IColorVariants)[] = [
  'accentColor',
  'secondaryAccentColor',
  'headingTextColor',
  'bodyTextColor',
  'secondaryBackgroundColor',
  'containerColor',
  'backgroundColor',
];

const MODES = [
  { mode: EPaletteModes.Light, label: 'Light' },
  { mode: EPaletteModes.Dark, label: 'Dark' },
];

// written into the html as a plain hex when it's opaque, which every email client reads, else as the
// rgba() the templates write theme colors as
const toCssColor = (color: TColorItemArray) => (color[1] >= 1 ? color[0] : parseColor(color));

// the template's default colors, in the same slots as a theme's - a template has the one set, not one per mode
const templateRow = (templateId: string | undefined) => {
  const defaults = templateStaticDefaults[templateId ?? 'capri'] ?? templateStaticDefaults.capri;
  const colors = SWATCH_SLOTS.map((slot) => defaults[slot as keyof typeof defaults]).filter(
    (c): c is string => typeof c === 'string'
  );
  return { key: 'template', label: '', colors };
};

const sameColor = (a: string | undefined, b: string) =>
  !!a && chroma.valid(a) && chroma.valid(b) && chroma(a).hex() === chroma(b).hex();

/**
 * The text widgets' color panel - the theme's colors in each mode, or any color. Passed to Quill as its
 * renderColorPanel, in place of its own swatch grid.
 */
export default function QuillColorPanel({ format, value, onChange }: QuillColorPanelProps) {
  const theme = useAppSelector(selectCampaignEditorTheme);
  const templateId = useAppSelector(selectCampaignEditorCurrentTemplateId);

  const rows = theme
    ? MODES.map(({ mode, label }) => {
        const palette = theme.colors[mode] ?? (mode === theme.theme_mode ? theme.colors : undefined);
        const colors = SWATCH_SLOTS.map((slot) => palette?.[slot]).filter((c): c is TColorItemArray => !!c);
        return { key: mode, label, colors: colors.map(toCssColor) };
      }).filter((row) => row.colors.length)
    : // without a theme the email renders in the template's own colors, so those are the ones to offer
      [templateRow(templateId)].filter((row) => row.colors.length);

  // for now, no custom color panel, so just default to theme, but replace with init value below once there is
  const [tab, setTab] = useState<Tab>('theme');

  // opens on whichever tab holds the selection's color - a color from outside the theme is a custom one
  // const [tab, setTab] = useState<Tab>(() =>
  //   !rows.length || (value && !rows.some((row) => row.colors.some((c) => sameColor(value, c)))) ? 'custom' : 'theme'
  // );

  return (
    <div className={classes.panel} aria-label={format === 'color' ? 'Font color' : 'Text background color'}>
      <div className={classes.pillTabs}>
        <PillTabs value={tab} tabs={TABS} onChange={(tab) => setTab(tab as Tab)} />
      </div>

      {tab === 'theme' ? (
        <div className={classes.swatches}>
          {rows.length ? (
            rows.map((row) => (
              <div key={row.key} className="flex-column gap-4">
                <Text className="L2" color="var(--global-ico-text-3)">
                  {row.label}
                </Text>
                <div className={classes.row}>
                  {row.colors.map((color, i) => (
                    <button
                      key={`${color}-${i}`}
                      type="button"
                      className={classNames(classes.swatch, { [classes.selected]: sameColor(value, color) })}
                      style={{ backgroundColor: color }}
                      title={color}
                      aria-label={`${row.label} ${color}`}
                      aria-pressed={sameColor(value, color)}
                      onClick={() => {
                        if (sameColor(value, color)) {
                          onChange(false);
                        } else {
                          onChange(color);
                        }
                      }}
                    />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <Text className="P2" color="var(--global-ico-text-3)">
              There are no colors to pick from.
            </Text>
          )}
        </div>
      ) : (
        // there is no color picker design that fits in this custom color swatch panel, so
        // until there is, putting this into "Coming Soon" mode since the stock color picker we have
        // is way too freaking big to fit into this

        // <ColorPicker
        //   value={value ?? '#000000'}
        //   width="100%"
        //   boxShadow="none"
        //   onChange={(result) => onChange(result.opacity >= 1 ? result.hex : result.rgba)}
        // />
        <div className={classes.comingSoon}>
          <ComingSoonImage />
        </div>
      )}
    </div>
  );
}
